import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Question, DownloadSimple, FilePdf, FileDoc, Sparkle, MagnifyingGlass } from "@phosphor-icons/react";

export default function InvestorQATab({ doc, onGenerate, busy }) {
  const qa = doc.qa || [];
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  const categories = useMemo(() => Array.from(new Set(qa.map((a) => a.category))).filter(Boolean), [qa]);
  const filtered = useMemo(() => qa.filter((a) => (!cat || a.category === cat) && (!q || (a.question + " " + a.answer).toLowerCase().includes(q.toLowerCase()))), [qa, q, cat]);

  const download = async (fmt) => {
    const t = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      const resp = await api.get(`/investment-docs/${doc.id}/export/qa.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data); const a = document.createElement("a"); a.href = url;
      a.download = `${(doc.company_name || "orixa-invest").replace(/\s+/g, "-").toLowerCase()}-investor-qa.${fmt}`;
      a.click(); URL.revokeObjectURL(url); toast.success(`${fmt.toUpperCase()} downloaded`, { id: t });
    } catch { toast.error("Download failed", { id: t }); }
  };

  if (qa.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-12 text-center card-elev" data-testid="qa-empty">
        <div className="mx-auto h-14 w-14 rounded-full bg-teal-soft text-teal grid place-items-center mb-4">
          <Question size={28} weight="duotone" />
        </div>
        <div className="font-display text-2xl tracking-tight mb-2">Generate 100 institutional Q&A</div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">Prepare for your investor meetings with 100 specific questions and suggested institutional answers.</p>
        <button onClick={onGenerate} disabled={busy} className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 card-elev" data-testid="qa-generate-btn">
          <Sparkle size={14} weight="bold" /> Generate Q&A
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="idoc-tab-qa-body">
      <div className="lg:col-span-3 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search questions or answers…"
              className="w-full bg-card border border-border rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
              data-testid="qa-search"
            />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2.5 text-sm" data-testid="qa-category">
            <option value="">All categories ({qa.length})</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="space-y-3">
          {filtered.map((a, i) => (
            <details key={i} className="group bg-card border border-border rounded-xl p-5 card-elev" data-testid={`qa-item-${i}`}>
              <summary className="cursor-pointer flex items-start gap-3 list-none">
                <div className="flex-shrink-0 h-7 w-7 rounded-md bg-teal-soft text-teal text-xs font-bold grid place-items-center">{i + 1}</div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{a.category}</div>
                  <div className="font-semibold text-foreground mt-0.5">{a.question}</div>
                </div>
              </summary>
              <div className="pt-3 mt-3 border-t border-border/50 text-sm leading-relaxed text-foreground pl-10">
                {a.answer}
              </div>
            </details>
          ))}
        </div>
      </div>

      <aside className="lg:col-span-1 lg:sticky lg:top-6 self-start">
        <div className="bg-card border border-border rounded-2xl p-5 card-elev space-y-2">
          <div className="text-overline text-muted-foreground mb-2">Downloads</div>
          <button onClick={() => download("pdf")} className="w-full inline-flex items-center justify-between border border-border rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="qa-download-pdf">
            <span className="inline-flex items-center gap-2"><FilePdf size={16} weight="duotone" className="text-primary" /> PDF</span>
            <DownloadSimple size={14} weight="bold" />
          </button>
          <button onClick={() => download("docx")} className="w-full inline-flex items-center justify-between border border-border rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="qa-download-docx">
            <span className="inline-flex items-center gap-2"><FileDoc size={16} weight="duotone" className="text-primary" /> DOCX</span>
            <DownloadSimple size={14} weight="bold" />
          </button>
          <div className="mt-3 pt-3 border-t border-border">
            <button onClick={onGenerate} disabled={busy} className="w-full text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 justify-center" data-testid="qa-regenerate">
              <Sparkle size={12} /> Regenerate answers
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
